/**
 * Speakable structured data (JSON-LD) for voice assistant optimization
 * Marks content sections suitable for text-to-speech readback
 * Used by Google Assistant and similar voice-based answer engines
 * Follows Schema.org SpeakableSpecification
 */

import React from 'react';
import { JSONLDScript } from './jsonld-script';

interface SpeakableStructuredDataProps {
  /** Page URL */
  url: string;
  /** Page title/headline */
  headline: string;
  /** CSS selectors for speakable content sections */
  cssSelectors?: string[];
}

/**
 * Speakable structured data component
 *
 * Marks specific content sections as suitable for voice assistant readback.
 * This helps Google Assistant and other voice engines identify which parts
 * of the page to read aloud when answering voice queries.
 *
 * @example
 * ```tsx
 * <SpeakableStructuredData
 *   url="https://emailferret.io/gmail-spam-filter"
 *   headline="Gmail Spam Filter Enhancement"
 *   cssSelectors={['[data-speakable="intro"]', '[data-speakable="summary"]']}
 * />
 * ```
 */
export function SpeakableStructuredData({
  url,
  headline,
  cssSelectors = ['[data-speakable="intro"]', '[data-speakable="summary"]'],
}: SpeakableStructuredDataProps) {
  const speakableSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: headline,
    url: url,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: cssSelectors,
    },
  };

  return <JSONLDScript data={speakableSchema} id="speakable-structured-data" />;
}
